import { useRef } from 'react';
import { useLoading } from '~/hooks';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import { filterObjectKeys } from '~/utils/Object';
import { getShouldUsingCredentialServiceEndpointForLicense } from '~/growthbook';
import { useAppSelector } from '~/hooks/useAppSelector';
import { IJob, ILicense, IUser, ProfessionTypeEnum } from '~/models';
import { formatYears } from '~/commonComponents/CommonFunctions';
import { removePrefixes } from '../utils';
import { useCheckPaymentExpiration } from '~/components/Inbox/Notification/Reminders/hooks/useCheckPaymentExpiration';

export const useCandidate = (user: IUser, candidate: IUser, job: IJob | null) => {
  const shouldUsingCredentialServiceEndpointForLicense = useRef(
    getShouldUsingCredentialServiceEndpointForLicense(),
  ).current;

  const { isLoading: isLoadingLicense } = useLoading(['FETCH_CANDIDATE_LICENSES_ACTION']);
  const candidatesLicenseV2: ILicense | null = useAppSelector((state) =>
    candidate?.id ? state.job.candidatesLicenseV2?.[candidate?.id] : null,
  );
  const candidateLicense = shouldUsingCredentialServiceEndpointForLicense
    ? candidatesLicenseV2
    : candidate?.license;

  const { payments } = useCheckPaymentExpiration();
  const hasValidPayment = user?.is_pre_approved || payments?.length;

  const professionalInfo = candidate?.professional_info;
  const officeInsurances = user?.office_info?.insurances;
  const candidateEducation = professionalInfo?.education;
  const candidateProfession = candidateLicense?.profession;
  const profession = (job?.profession || candidateProfession) as ProfessionTypeEnum;
  const educationInfo = candidateEducation?.[profession];
  const candidateInsurances = professionalInfo?.insurances;
  const userSpecialties = Object.keys(
    filterObjectKeys(educationInfo?.specialtiesAndProcedures, 'specialty'),
  );

  const userProcedures = Object.keys(
    filterObjectKeys(educationInfo?.specialtiesAndProcedures, 'procedure'),
  );

  const specialtiesAndProcedures = educationInfo?.specialtiesAndProcedures ?? {};
  const professionProcedures = removePrefixes(educationInfo?.specialtiesAndProcedures ?? {});

  const isSpecialty =
    job?.skills?.[0]?.code?.includes('specialty') ||
    job?.specialty?.toLowerCase()?.includes('specialty');
  const specialtyMatch = userSpecialties.includes(job?.specialty || '') && isSpecialty;

  const insuranceMismatch =
    officeInsurances?.length &&
    candidateInsurances?.length &&
    !officeInsurances?.some((insurance) => candidateInsurances?.includes(insurance));
  const professionName = (() => {
    const subProfession = candidateLicense?.subProfession;

    if (subProfession === UserSubProfession.EFDA) {
      return 'Expanded Functions DA';
    }

    return candidateLicense?.professionName;
  })();

  const softwares = candidateEducation?.[profession]?.softwares;

  const licenseNumber = candidateLicense?.credentials?.find(
    ({ entry_field }) => entry_field?.license,
  )?.entry_field?.license;
  const issueDate = candidateLicense?.credentials?.find(
    ({ entry_field }) => entry_field?.issue_date,
  )?.entry_field?.issue_date;

  const nationalProviderIdentifier = professionalInfo?.nationalProviderIdentifier;
  const shouldShowNpi = candidateLicense?.profession === UserProfession.DN;
  const licenseState = candidateLicense?.state;

  const yearsOfExperience =
    professionalInfo?.exactYearsOfExperience != null
      ? formatYears(professionalInfo.exactYearsOfExperience)
      : candidate?.years_of_experience;

  return {
    userSpecialties,
    userProcedures,
    candidateInsurances,
    // professionProcedures is only processed specialtiesAndProcedures, use only for showing
    professionProcedures,
    specialtiesAndProcedures,
    specialtyMatch,
    insuranceMismatch,
    softwares,
    licenseNumber: hasValidPayment ? licenseNumber : '******',
    licenseState,
    issueDate,
    nationalProviderIdentifier,
    shouldShowNpi,
    professionName,
    isLoadingLicense,
    yearsOfExperience,
  };
};
